// src/features/records/components/NoteView/NoteView.js
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './NoteView.module.css';
import RecordCard from './RecordCard';
import RecordErrorBoundary from '../RecordErrorBoundary';
import RecordEditor from '../RecordEditor';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import Page from '../../../../components/Page';
import { fetchRecordsByIds } from '../../services/core/queries';
import { getRecordAttachments } from '../../services/attachments/queries';
import { setRecordActive, clearAttachmentCache } from '../../services/attachments/state';
import { useDeleteRecord } from '../../hooks/useRecordMutations';
import useUserProfiles from '../../hooks/useUserProfiles';
import useRecordDetail from '../../hooks/useRecordDetail';
import useRecordList from '../../hooks/useRecordList';
import useFilterStore from '../../stores/useFilterStore';
import useActiveRecords from '../../hooks/useActiveRecords';
import { useToast } from '../../../../hooks/useToast';
import { useRecordsLayoutContext } from '../../hooks/useRecordsLayoutContext';
import mapRecordsErrorToKey from '../../utils/recordsErrorMapper';

const NoteView = () => {
  const { searchQuery, onLinkToRecord } = useRecordsLayoutContext();
  const { t } = useTranslation();
  const toast = useToast();
  const deleteRecord = useDeleteRecord();
  const { openDetail, selectedRecord } = useRecordDetail();
  const scope = useFilterStore((state) => state.scope);

  // Use Edge-Indexed list hook
  const {
    records,
    loading,
    loadingMore,
    error,
    hasMore,
    fetchNextPage,
    refetch
  } = useRecordList({
    viewType: 'note',
    scope,
    sortSpec: { key: 'updatedAt', dir: 'desc' },
    searchTerm: searchQuery,
    pageSize: 30
  });
  
  const { activeRecordIds, observeRecord, unobserveRecord } = useActiveRecords({
    rootMargin: '200px', // Pre-fetch 200px before entering viewport
    threshold: 0.05 // 5% visibility threshold
  });
  const [editingRecord, setEditingRecord] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  
  // Track which records have had their attachments fetched
  const [attachmentsFetched, setAttachmentsFetched] = useState(new Set());
  
  // Use ref to access latest records without triggering re-renders
  const recordsRef = useRef(records);
  useEffect(() => {
    recordsRef.current = records;
  }, [records]);

  // Extract unique user IDs from records
  const userIds = useMemo(() => {
    const ids = new Set();
    records.forEach(record => {
      if (record.access?.owner) {
        ids.add(record.access.owner);
      }
    });
    return Array.from(ids);
  }, [records]);

  // Fetch user profiles for all record authors
  const { profiles: userProfiles, loading: profilesLoading } = useUserProfiles(userIds);

  // Effect to fetch attachments for active records
  useEffect(() => {
    const fetchAttachmentsForActiveRecords = async () => {
      for (const recordId of activeRecordIds) {
        // Skip if already fetched
        if (attachmentsFetched.has(recordId)) continue;
        
        // Use ref to get latest records without triggering effect
        const record = recordsRef.current.find(r => r.id === recordId);
        if (!record || !record.content?.attachmentsSummary) continue;
        
        try {
          // Mark as fetched immediately to prevent duplicate fetches
          setAttachmentsFetched(prev => new Set([...prev, recordId]));
          
          // Fetch attachments with version-aware caching
          const attachments = await getRecordAttachments(
            recordId, 
            record.content.attachmentsSummary.version || 0,
            true // Enable real-time updates for active records
          );
          
          // Update the record with full attachments locally
          // React Query will handle cache updates through refetch
          record.content.attachments = attachments;
          
          // Mark record as active in service
          setRecordActive(recordId, true);
        } catch (error) {
          console.error(`Error fetching attachments for record ${recordId}:`, error);
          
          // Remove from fetched set to allow retry
          setAttachmentsFetched(prev => {
            const next = new Set(prev);
            next.delete(recordId);
            return next;
          });
        }
      }
    };

    fetchAttachmentsForActiveRecords();
  }, [activeRecordIds, attachmentsFetched]);

  // Cleanup: stop listeners for inactive records
  useEffect(() => {
    const allRecordIds = new Set(records.map(r => r.id));
    
    // Find records that are no longer active or no longer in the list
    attachmentsFetched.forEach(recordId => {
      if (!activeRecordIds.has(recordId) || !allRecordIds.has(recordId)) {
        setRecordActive(recordId, false);
      }
    });
  }, [activeRecordIds, records, attachmentsFetched]);

  // Clean up all attachment listeners on unmount
  useEffect(() => {
    return () => {
      // Clear all attachment subscriptions and caches when component unmounts
      clearAttachmentCache();
    };
  }, []);


  // When search is active, records are already server-filtered and ranked by Algolia.
  // No client-side filtering needed - trust the server results.
  const filteredRecords = records;
  const listErrorKey = error
    ? mapRecordsErrorToKey(error, { fallbackKey: 'records.views.note.errors.load_failed' })
    : null;

  const handleLinkToRecord = useCallback((recordId) => {
    if (!recordId || !onLinkToRecord) return;
    onLinkToRecord(recordId);
  }, [onLinkToRecord]);

  const handleEditRecord = useCallback(async (record) => {
    // If we only have a preview, fetch the full SSOT from Firestore
    if (record?._isPreview || !record?.version) {
      try {
        const fullRecords = await fetchRecordsByIds([record.id]);
        if (fullRecords && fullRecords.length > 0) {
          setEditingRecord(fullRecords[0]);
          return;
        }
      } catch (error) {
        console.error('Failed to hydrate record for editing:', error);
        toast.error(
          t(
            mapRecordsErrorToKey(error, {
              fallbackKey: 'records.views.note.errors.edit_load_failed'
            })
          )
        );
        return;
      }
    }
    // Already have full record
    setEditingRecord(record);
  }, [t, toast]);

  const handleSaveRecord = useCallback(() => {
    // RecordEditor already saved the record, this is just a notification callback
    // Close the editor and show success message
    setEditingRecord(null);
    toast.success(t('records.views.note.toasts.saved_success'));
  }, [t, toast]);

  const handleDeleteRecord = useCallback((record) => {
    // Show confirmation modal instead of window.confirm
    setDeleteConfirmation(record);
  }, []);

  const confirmDelete = useCallback(async () => {
    const recordToDelete = deleteConfirmation;
    setDeleteConfirmation(null);

    if (!recordToDelete) return;

    // Close the RecordDetail modal FIRST if the deleted record is being viewed
    // This prevents the modal from trying to render a deleted record
    if (selectedRecord && selectedRecord.id === recordToDelete.id) {
      // The context will handle closing when the record is deleted
    }
    
    // Use mutation hook for deleting
    deleteRecord.mutate(recordToDelete.id, {
      onSuccess: () => {
        // Show success toast notification
        toast.success(t('records.views.note.toasts.deleted_success'));
      },
      onError: (error) => {
        console.error('Error deleting record:', error);

        toast.error(
          t(
            mapRecordsErrorToKey(error, {
              fallbackKey: 'records.views.note.errors.delete_failed'
            })
          )
        );
      }
    });
    // No need to refetch - mutation hook handles cache invalidation
  }, [deleteConfirmation, selectedRecord, t, toast, deleteRecord]);

  const cancelDelete = useCallback(() => {
    setDeleteConfirmation(null);
  }, []);


  // Memoized scroll handler to prevent memory leaks
  const handleScroll = useCallback(() => {
    if (loadingMore || !hasMore) return;
    
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
    const threshold = 100; // pixels from bottom
    
    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      fetchNextPage();
    }
  }, [fetchNextPage, loadingMore, hasMore]);

  // Infinite scroll handler
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <Page width="default">
      {/* Error Message */}
      {error && (
        <div className={styles.errorContainer}>
          <div className={styles.errorMessage}>
            <span>{listErrorKey ? t(listErrorKey) : t('records.views.note.errors.load_failed')}</span>
            <button 
              onClick={() => refetch()} 
              className={styles.retryButton}
            >
              {t('common.retry')}
            </button>
          </div>
        </div>
      )}

      {/* Records Container */}
      <div className={styles.recordsContainer}>
        {loading ? (
          <div className={styles.loadingContainer}>
            {[...Array(3)].map((_, index) => (
              <div key={index} className={styles.skeletonCard}>
                <div className={styles.skeletonHeader}>
                  <div className={styles.skeletonAvatar} />
                  <div className={styles.skeletonInfo}>
                    <div className={styles.skeletonName} />
                    <div className={styles.skeletonMeta} />
                  </div>
                </div>
                <div className={styles.skeletonTitle} />
                <div className={styles.skeletonBody} />
              </div>
            ))}
          </div>
        ) : filteredRecords.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </div>
            <h3 className={styles.emptyTitle}>{t('records.views.note.empty.title')}</h3>
            <p className={styles.emptyText}>{t('records.views.note.empty.description')}</p>
          </div>
        ) : (
          <>
            {filteredRecords.map(record => (
              <RecordErrorBoundary key={record.id}>
                <RecordCard
                  record={record}
                  authorProfile={userProfiles[record.access?.owner]}
                  profileLoading={profilesLoading}
                  onLinkToRecord={handleLinkToRecord}
                  onEdit={handleEditRecord}
                  onDelete={handleDeleteRecord}
                  onDetailView={openDetail}
                  observeRecord={observeRecord}
                  unobserveRecord={unobserveRecord}
                />
              </RecordErrorBoundary>
            ))}
            
            {/* Load More Indicator */}
            {loadingMore && (
              <div className={styles.loadingMore}>
                <div className={styles.spinner}></div>
                <span>{t('records.views.note.loading_more')}</span>
              </div>
            )}
            
            {/* End of results indicator */}
            {!hasMore && records.length > 0 && (
              <div className={styles.endOfResults}>
                <span>{t('records.views.note.end_of_results')}</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Record Editor Modal */}
      {editingRecord && (
        <RecordEditor
          record={editingRecord}
          isOpen={!!editingRecord}
          onClose={() => setEditingRecord(null)}
          onSave={handleSaveRecord}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <ConfirmationModal
          isOpen={!!deleteConfirmation}
          onClose={cancelDelete}
          onConfirm={confirmDelete}
          title={t('records.views.note.delete_modal.title')}
          message={t('records.views.note.delete_modal.message', {
            title: deleteConfirmation.content?.title || t('records.views.note.delete_modal.fallback_title')
          })}
          confirmText={t('records.actions.delete')}
          cancelText={t('common.cancel')}
        />
      )}

    </Page>
  );
};

export default NoteView;
